import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  getAllTours,
  getAllToursAdmin,
  getAllToursFast,
  getCollaboratedTours,
} from 'app/services/firebase/functions';
import { isAdminUser } from 'app/constants';
import { Tour, User } from 'app/types';

interface ToursState {
  loading: boolean;
  data: Array<Tour>;
}

const initialState: ToursState = {
  loading: false,
  data: [],
};

export const fetchTours = createAsyncThunk(
  'tours/fetchTours',
  async (user: User) => {
    // Admin sees everything via the dedicated GCF and skips collab merge.
    if (isAdminUser(user.email)) {
      return getAllToursAdmin();
    }

    const ownedPromise =
      process.env.REACT_APP_USE_FAST_TOURS === 'true'
        ? getAllToursFast(user.full_id)
        : getAllTours(user.full_id);

    // If the collab GCF is unreachable for any reason, fall back to owned-only
    // so the dashboard never breaks because of a degraded collab path.
    const collabPromise = getCollaboratedTours().catch((err) => {
      console.warn('[fetchTours] collaborated tours failed', err);
      return [] as Tour[];
    });

    const [owned, collab] = await Promise.all([ownedPromise, collabPromise]);
    const seen = new Set(owned.map((t) => t.full_id));
    return [...owned, ...collab.filter((t) => !seen.has(t.full_id))];
  },
);

const toursSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    setTours: (state, action: PayloadAction<Tour[]>) => {
      state.data = action.payload;
    },
    updateTour: (
      state,
      action: PayloadAction<{
        tourId: string;
        data: Partial<Tour>;
      }>,
    ) => {
      const tourIndex = state.data.findIndex(
        (tour) => tour.full_id === action.payload.tourId,
      );

      console.log('tourId: ', action.payload.tourId);

      if (tourIndex !== -1) {
        state.data.splice(tourIndex, 1, {
          ...state.data[tourIndex],
          ...action.payload.data,
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTours.pending, (state, action) => {
        console.log('tours/fetchTours/pending', action);
        state.loading = true;
      })
      .addCase(fetchTours.fulfilled, (state, action) => {
        console.log('tours/fetchTours/fulfilled', action);
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchTours.rejected, (state, action) => {
        console.log('tours/fetchTours/rejected', action);
        state.loading = false;
      });
  },
});

export const { setTours, updateTour } = toursSlice.actions;

export default toursSlice.reducer;
